import React, { Component } from "react";
import { Fade } from "react-awesome-reveal";
import Particles from "../../Background/Particles";
import type { MainProfile } from "../../types/resume";

interface AboutProps {
  data: MainProfile;
}

class About extends Component<AboutProps> {
  private popupInitialized = false;
  componentDidMount() {
    this.initPopup();
  }

  componentDidUpdate(prevProps: Readonly<AboutProps>) {
    if (this.props.data !== prevProps.data) {
      this.initPopup();
    }
  }

  componentWillUnmount() {
    // Clean up any popup listeners to avoid duplicate handlers
    if (window.$ && window.$.fn && window.$.fn.magnificPopup) {
      try {
        window.$(".image.popup").off("click");
        // remove any stored plugin data if present
        window.$(".image.popup").removeData && window.$(".image.popup").removeData("magnificPopup");
      } catch (e) {
        // swallow cleanup errors
      }
    }
    this.popupInitialized = false;
  }

  initPopup = () => {
    if (!window.$ || !window.$.fn || !window.$.fn.magnificPopup) return;

    // avoid initializing multiple times (prevents adding duplicate listeners)
    if (this.popupInitialized) return;

    // ensure previous handlers are removed before re-initializing
    try {
      window.$('.image.popup').off('click');
      window.$('.image.popup').removeData && window.$('.image.popup').removeData('magnificPopup');
    } catch (e) {
      // ignore cleanup errors
    }

    setTimeout(() => {
      window.$('.image.popup').magnificPopup({
        type: 'image',
        closeOnContentClick: true,
        mainClass: 'mfp-img-mobile',
        image: { verticalFit: true },
      });
      this.popupInitialized = true;
    }, 500);
  };

  render() {
    if (!this.props.data) return null;

    const { image, bio, address, phone, email, resumeDownload } =
      this.props.data;
    const profilepic = "image/" + image;

    return (
      <section
        id="about"
        className="relative flex min-h-[90vh] items-center justify-center overflow-hidden px-3 py-20 text-white sm:px-6 lg:px-8"
      >
        <div className="absolute inset-0">
          <Particles
            particleColors={["#ffffff"]}
            particleCount={80}
            particleSpread={20}
            speed={0.05}
            particleBaseSize={40}
            alphaParticles={false}
            disableRotation={true}
            pixelRatio={1}
          />
        </div>

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.28),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(34,211,238,0.24),_transparent_35%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_45%,rgba(255,255,255,0.04))]" />

        <div className="relative z-10 w-full max-w-[92%] lg:max-w-[85%] px-1 sm:px-2">
          <Fade direction="up" duration={1100} triggerOnce>
            <div className="relative overflow-hidden rounded-[36px] border border-white/15 bg-slate-950/70 p-6 shadow-[0_30px_100px_rgba(0,0,0,0.45)] backdrop-blur-2xl md:p-10 lg:p-12">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-cyan-400/20" />
              <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-orange-500/20 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-cyan-400/20 blur-3xl" />

              <div className="relative z-10 flex flex-col gap-8 lg:flex-row lg:items-stretch">
                <div className="flex-shrink-0 self-center lg:self-auto flex items-center">
                  <div className="relative w-full rounded-[30px] border border-white/20 bg-white/10 p-3 shadow-[0_25px_60px_rgba(0,0,0,0.35)]">
                    <div className="absolute -right-3 -top-3 h-16 w-16 rounded-full bg-orange-500/35 blur-2xl" />
                    <a
                      className="image popup block overflow-hidden rounded-[24px] transition duration-500 hover:-translate-y-1"
                      href={profilepic}
                    >
                      <img
                        className="h-72 w-80 object-cover sm:h-80 sm:w-96 lg:h-[460px] lg:w-[400px]"
                        src={profilepic}
                        alt="Profile"
                        width={400}
                        height={460}
                      />
                    </a>
                  </div>
                </div>

                <div className="flex-1 space-y-7 flex flex-col justify-between">
                  <div className="space-y-4">
                    <p className="text-sm font-semibold uppercase tracking-[0.4em] text-orange-400">
                      Get to know me
                    </p>
                    <h2 className="text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
                      About <span className="bg-gradient-to-r from-orange-500 to-cyan-400 bg-clip-text text-transparent">Me</span>
                    </h2>
                    <p className="max-w-none text-base leading-8 text-slate-300 sm:text-lg lg:text-xl">
                      {bio}
                    </p>
                  </div>

                  <div className="mt-auto grid gap-6 xl:grid-cols-[1.4fr_0.6fr]">
                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-6 shadow-lg shadow-black/20 backdrop-blur-lg">
                      <h3 className="mb-6 text-2xl font-semibold text-orange-400">
                        Contact Details
                      </h3>

                      <div className="space-y-4 text-base text-slate-200 sm:text-lg">
                        <div className="flex items-start gap-3 rounded-2xl bg-slate-900/45 p-4">
                          <span className="mt-1 text-xl text-orange-400">
                            <i className="fa-solid fa-location-dot"></i>
                          </span>
                          <a
                            href={`https://www.google.com/maps?q=${encodeURIComponent(
                              `${address.street}, ${address.city}, ${address.state} ${address.zip}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition hover:text-orange-400 hover:underline"
                          >
                            <span className="block font-medium">Location</span>
                            <span className="text-slate-300">
                              {address.street}
                              <br />
                              {address.city}, {address.state} {address.zip}
                            </span>
                          </a>
                        </div>

                        <div className="flex items-center gap-3 rounded-2xl bg-slate-900/45 p-4">
                          <span className="text-xl text-orange-400">
                            <i className="fa-brands fa-whatsapp"></i>
                          </span>
                          <a
                            href={`https://wa.me/${phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition hover:text-orange-400 hover:underline"
                          >
                            {phone}
                          </a>
                        </div>

                        <div className="flex items-center gap-3 rounded-2xl bg-slate-900/45 p-4">
                          <span className="text-xl text-orange-400">
                            <i className="fa-solid fa-envelope"></i>
                          </span>
                          <a
                            href={`mailto:${email}`}
                            className="transition hover:text-orange-400 hover:underline"
                          >
                            {email}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col justify-center gap-4 rounded-[24px] border border-white/10 bg-gradient-to-br from-orange-500/20 to-cyan-400/15 p-6 shadow-lg shadow-black/25">
                      <a
                        href={resumeDownload}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-3 rounded-2xl bg-orange-500 px-5 py-3.5 font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-orange-400"
                      >
                        <i className="fa fa-download" />
                        <span>Download Resume</span>
                      </a>

                      <a
                        href="/contact"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-3 rounded-2xl border border-cyan-400/40 bg-cyan-400/10 px-5 py-3.5 font-semibold text-cyan-200 transition duration-200 hover:-translate-y-0.5 hover:bg-cyan-400/20"
                      >
                        <i className="fa fa-envelope" />
                        <span>Contact</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Fade>
        </div>
      </section>
    );
  }
}

export default About;
